/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { all, call, cancel, delay, fork, put, select, take, takeEvery, takeLatest, race } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
  ActionRequest,
  ConsumptionMeta,
  Milliseconds,
  Seconds,
  BookmarksConfigs,
  Specs,
  Ducks,
  AssetUtils,
} from '@wit/vtv-x-foundation-lib';
import { StoreState } from 'store/store';
import * as Distributor from './distributor';
import { Creators as DelayCreators } from './delay';

// "playing-buffering-playing" workaround
// this timeout help us detect the following fast sequence of events: playing -> buffering -> playing
// this is currently doed by the player for example after a seek success and should not be reflected in consumption
// to avoid that if a buffering event comes after a play with the same timestamp we delay it for 500ms, and if another play comes in this interval we cancel both to only keep the first play
const PLAYING_BUFFER_PLAYING_DELAY = 500;

// Prevent too many volume changes
const VOLUME_DELAY = 500;

export enum ActionTypes {
  PLAY = 'consumption/engine/PLAY',
  PLAYING = 'consumption/engine/PLAYING',
  PAUSE = 'consumption/engine/PAUSE',
  STOP = 'consumption/engine/STOP',
  ZAP = 'consumption/engine/ZAP',
  SCRUB_START = 'consumption/engine/SCRUB_START',
  SCRUB_END = 'consumption/engine/SCRUB_END',
  START_OVER = 'consumption/engine/START_OVER',
  LOAD_START = 'consumption/engine/LOAD_START',
  LOAD_END = 'consumption/engine/LOAD_END',
  BITRATE_CHANGE = 'consumption/engine/BITRATE_CHANGE',
  ROLLOVER = 'consumption/engine/ROLLOVER',
  VOLUME = 'consumption/engine/VOLUME',
  MUTE = 'consumption/engine/MUTE',
}

type Play = ActionRequest<ActionTypes.PLAY, ConsumptionMeta>;
type Playing = ActionRequest<ActionTypes.PLAYING, ConsumptionMeta>;
type Pause = ActionRequest<ActionTypes.PAUSE, ConsumptionMeta>;
type Stop = ActionRequest<ActionTypes.STOP, ConsumptionMeta>;
type Zap = ActionRequest<ActionTypes.ZAP, ConsumptionMeta>;
type ScrubStart = ActionRequest<ActionTypes.SCRUB_START, ConsumptionMeta>;
type ScrubEnd = ActionRequest<ActionTypes.SCRUB_END, ConsumptionMeta>;
type StartOver = ActionRequest<ActionTypes.START_OVER, ConsumptionMeta>;
type LoadStart = ActionRequest<ActionTypes.LOAD_START, ConsumptionMeta>;
type LoadEnd = ActionRequest<ActionTypes.LOAD_END, ConsumptionMeta>;
type BitrateChange = ActionRequest<ActionTypes.BITRATE_CHANGE, ConsumptionMeta>;
type Rollover = ActionRequest<ActionTypes.ROLLOVER, ConsumptionMeta>;
type Volume = ActionRequest<ActionTypes.VOLUME, ConsumptionMeta>;
type Mute = ActionRequest<ActionTypes.MUTE, ConsumptionMeta>;

type Actions =
  | Play
  | Playing
  | Pause
  | Stop
  | Zap
  | ScrubStart
  | ScrubEnd
  | StartOver
  | LoadStart
  | LoadEnd
  | BitrateChange
  | Rollover
  | Volume
  | Mute;

const Actions = {
  play: (meta: ConsumptionMeta): Play => ({ type: ActionTypes.PLAY, meta }),
  playing: (meta: ConsumptionMeta): Playing => ({ type: ActionTypes.PLAYING, meta }),
  pause: (meta: ConsumptionMeta): Pause => ({ type: ActionTypes.PAUSE, meta }),
  stop: (meta: ConsumptionMeta): Stop => ({ type: ActionTypes.STOP, meta }),
  zap: (meta: ConsumptionMeta): Zap => ({ type: ActionTypes.ZAP, meta }),
  scrubStart: (meta: ConsumptionMeta): ScrubStart => ({ type: ActionTypes.SCRUB_START, meta }),
  scrubEnd: (meta: ConsumptionMeta): ScrubEnd => ({ type: ActionTypes.SCRUB_END, meta }),
  startOver: (meta: ConsumptionMeta): StartOver => ({ type: ActionTypes.START_OVER, meta }),
  loadStart: (meta: ConsumptionMeta): LoadStart => ({ type: ActionTypes.LOAD_START, meta }),
  loadEnd: (meta: ConsumptionMeta): LoadEnd => ({ type: ActionTypes.LOAD_END, meta }),
  bitrateChange: (meta: ConsumptionMeta): BitrateChange => ({ type: ActionTypes.BITRATE_CHANGE, meta }),
  rollover: (meta: ConsumptionMeta): Rollover => ({ type: ActionTypes.ROLLOVER, meta }),
  volume: (meta: ConsumptionMeta): Volume => ({ type: ActionTypes.VOLUME, meta }),
  mute: (meta: ConsumptionMeta): Mute => ({ type: ActionTypes.MUTE, meta }),
};

export const Creators = {
  play: Actions.play,
  playing: Actions.playing,
  pause: Actions.pause,
  stop: Actions.stop,
  zap: Actions.zap,
  scrubStart: Actions.scrubStart,
  scrubEnd: Actions.scrubEnd,
  startOver: Actions.startOver,
  loadStart: Actions.loadStart,
  loadEnd: Actions.loadEnd,
  bitrateChange: Actions.bitrateChange,
  rollover: Actions.rollover,
  volume: Actions.volume,
  mute: Actions.mute,
};

// REDUCERS
interface StateMeta extends ConsumptionMeta {
  type: ActionTypes;
}

interface StoreMeta extends StateMeta {
  timestamp: Milliseconds;
}

export interface State {
  previousAssetState: StoreMeta | undefined;
  previousState: StoreMeta | undefined;
  currentState: StoreMeta | undefined;
  playerActive: boolean;
  playCalls: number;
}

const initialState: State = {
  previousAssetState: undefined,
  previousState: undefined,
  currentState: undefined,
  playerActive: false,
  playCalls: 0,
};

// AUX
function standardizeConsumptionMeta(meta: ConsumptionMeta): ConsumptionMeta {
  return { ...meta, location: Math.floor(meta.location) };
}

function isSameContent(previousState: StateMeta, currentState: StateMeta): boolean {
  if (previousState.asset === undefined && currentState.asset === undefined) {
    if (previousState.channel === undefined || currentState.channel === undefined) {
      return false;
    }
    return AssetUtils.isSameContent(previousState.channel, currentState.channel);
  }
  if (previousState.asset !== undefined && currentState.asset !== undefined) {
    return AssetUtils.isSameContent(previousState.asset, currentState.asset);
  }
  return false;
}

export default function programsReducer(state = initialState, action: Actions): State {
  switch (action.type) {
    case ActionTypes.PLAY:
    case ActionTypes.PLAYING:
    case ActionTypes.PAUSE:
    case ActionTypes.STOP:
    case ActionTypes.ZAP:
    case ActionTypes.SCRUB_START:
    case ActionTypes.SCRUB_END:
    case ActionTypes.START_OVER:
    case ActionTypes.LOAD_START:
    case ActionTypes.LOAD_END:
    case ActionTypes.ROLLOVER:
    case ActionTypes.VOLUME:
    case ActionTypes.MUTE: {
      const previousState = state.currentState;
      const currentState = { type: action.type, ...standardizeConsumptionMeta(action.meta), timestamp: Date.now() };
      const sameContent = previousState && isSameContent(previousState, currentState);
      let { playerActive, playCalls } = state;
      if (sameContent) {
        if (action.type === ActionTypes.PLAY) {
          playerActive = true;
        }
        if (playerActive && action.type === ActionTypes.PLAYING) {
          playCalls += 1;
        }
        return {
          ...state,
          previousState,
          currentState,
          playerActive,
          playCalls,
        };
      }
      return {
        previousAssetState: previousState,
        previousState: undefined,
        currentState,
        playerActive: false,
        playCalls: 0,
      };
    }
    case ActionTypes.BITRATE_CHANGE:
    default:
      return state;
  }
}

// SELECTORS
export const Selectors = {
  getState: (state: StoreState): State => state.consumption.engine,
};

// SAGAS

// USER ACTIONS

function* getMediaMark(isLive: boolean): SagaIterator<Seconds> {
  const configs: BookmarksConfigs | undefined = yield select(Ducks.Dms.Selectors.getBookmarks);
  return isLive
    ? configs?.linearMediaMark || Specs.DMS_LINEAR_MEDIA_MARK
    : configs?.vodMediaMark || Specs.DMS_VOD_MEDIA_MARK;
}

function isSameEvent(currentState: StoreMeta | undefined, previousState: StoreMeta | undefined): boolean {
  return (
    currentState?.fileId === previousState?.fileId &&
    currentState?.type === previousState?.type &&
    currentState?.location === previousState?.location
  );
}

function* runUserActionsEngineSaga(): SagaIterator {
  const { currentState, previousState, playCalls }: State = yield select(Selectors.getState);

  // If the play was not called yet do not send any events
  if (!currentState || playCalls === 0) {
    return;
  }

  // Don't send repeated events
  if (isSameEvent(currentState, previousState)) {
    return;
  }

  const { type, location, isLive } = currentState;
  const mediaMark: Seconds = yield call(getMediaMark, isLive);
  const playFn = location > 0 ? Distributor.play : Distributor.firstPlay;
  const previousAction = previousState?.type;

  // Rely on Playing event instead of Play, using play to check if the player is ready
  if (type === ActionTypes.PLAY) {
    return;
  }

  // "playing-buffering-playing" workaround (see PLAYING_BUFFER_PLAYING_DELAY comments)
  if (
    previousState &&
    type === ActionTypes.PLAYING &&
    previousAction === ActionTypes.LOAD_START &&
    currentState.timestamp - previousState.timestamp < PLAYING_BUFFER_PLAYING_DELAY
  ) {
    return;
  }

  // If it is the first call to play, delay it
  yield put(DelayCreators.cancel());
  if (type === ActionTypes.PLAYING && playCalls === 1) {
    yield put(DelayCreators.delay(mediaMark, playFn, { ...currentState, location: location + mediaMark }));
    return;
  }

  const beforeMediaMark = mediaMark > location;
  if (beforeMediaMark && playCalls === 1) {
    if (type === ActionTypes.STOP || type === ActionTypes.ZAP) {
      return;
    }
    yield call(Distributor.firstPlay, currentState);
  }

  switch (type) {
    case ActionTypes.PLAYING:
      yield call(playFn, currentState);
      break;
    case ActionTypes.PAUSE:
      if (previousAction !== ActionTypes.PAUSE) {
        yield call(Distributor.pause, currentState);
      }
      break;
    case ActionTypes.STOP:
      if (previousAction !== ActionTypes.STOP && previousAction !== ActionTypes.ZAP) {
        yield call(Distributor.stop, currentState);
      }
      break;
    case ActionTypes.ZAP:
      if (isLive) {
        if (previousState) {
          yield call(Distributor.stop, previousState);
          yield call(Distributor.zap, currentState, previousState);
        }
      }
      break;
    case ActionTypes.SCRUB_START:
      if (previousAction !== ActionTypes.PAUSE && previousAction !== ActionTypes.START_OVER) {
        yield call(Distributor.pause, currentState);
      }
      break;
    case ActionTypes.SCRUB_END:
      if (previousState && previousState.location !== currentState.location) {
        yield call(Distributor.seek, currentState, previousState);
      }
      break;
    case ActionTypes.START_OVER:
      yield call(Distributor.startOver, currentState);
      if (isLive) {
        yield call(Distributor.stop, currentState);
      } else if (previousAction !== ActionTypes.PAUSE) {
        yield call(Distributor.pause, currentState);
      }
      break;

    default:
      break;
  }
}

function* runVolumeActionsEngineSaga(): SagaIterator {
  const { currentState, previousState }: State = yield select(Selectors.getState);

  if (!currentState) {
    return;
  }

  const { type } = currentState;

  switch (type) {
    case ActionTypes.VOLUME: {
      yield delay(VOLUME_DELAY);
      yield call(Distributor.volumeChange, currentState, previousState);
      break;
    }
    case ActionTypes.MUTE: {
      yield call(Distributor.volumeChange, currentState, previousState);
      break;
    }
    default:
      break;
  }
}

// ASSET LOADING ENGINE SAGA

function* assetLoadingEngineSaga(next: StateMeta): SagaIterator {
  try {
    const { previousState }: State = yield select(Selectors.getState);
    // If is the first loading wait 3 Seconds before the first loading event
    if (!previousState || previousState.location === next.location) {
      yield delay(Specs.CONSUMPTION_MEDIA_LOAD_INTERVAL * 1000);
    }
    // "playing-buffering-playing" workaround (see PLAYING_BUFFER_PLAYING_DELAY comments)
    if (previousState && previousState.type === ActionTypes.PLAYING && previousState.location === next.location) {
      yield delay(PLAYING_BUFFER_PLAYING_DELAY);
    }
    while (true) {
      yield call(Distributor.load, next);
      yield delay(Specs.CONSUMPTION_MEDIA_LOAD_INTERVAL * 1000);
    }
  } finally {
    // load stopped
  }
}

function* startAssetLoadingEngineSaga(requestAction: Actions): SagaIterator {
  const { type, meta: dirtyMeta } = requestAction;
  const meta = standardizeConsumptionMeta(dirtyMeta);
  const assetLoadingTask = yield fork(assetLoadingEngineSaga, { type, ...meta });
  yield race({
    stop: take(ActionTypes.STOP),
    play: take(ActionTypes.PLAY),
    playing: take(ActionTypes.PLAYING),
    loadEnd: take(ActionTypes.LOAD_END),
  });
  yield cancel(assetLoadingTask);
}

// DIRECT ENGINE SAGA

function* runDirectEngineSaga(requestAction: Actions): SagaIterator {
  const { type, meta: dirtyMeta } = requestAction;
  const meta = standardizeConsumptionMeta(dirtyMeta);
  switch (type) {
    case ActionTypes.BITRATE_CHANGE: {
      yield call(Distributor.bitrateChange, meta);
      break;
    }
    case ActionTypes.ROLLOVER: {
      // TODO: deal with rollover
      break;
    }
    default: {
      break;
    }
  }
}

// ENGINE MAIN SAGA

export function* saga(): SagaIterator {
  yield all([
    takeEvery(
      [
        ActionTypes.PLAY,
        ActionTypes.PLAYING,
        ActionTypes.PAUSE,
        ActionTypes.STOP,
        ActionTypes.ZAP,
        ActionTypes.SCRUB_START,
        ActionTypes.SCRUB_END,
        ActionTypes.START_OVER,
      ],
      runUserActionsEngineSaga
    ),
    takeLatest([ActionTypes.VOLUME, ActionTypes.MUTE], runVolumeActionsEngineSaga),
    takeLatest([ActionTypes.LOAD_START], startAssetLoadingEngineSaga),
    takeEvery([ActionTypes.BITRATE_CHANGE, ActionTypes.ROLLOVER], runDirectEngineSaga),
  ]);
}
